'use client';

import React from 'react';
import WhatsAppSvg from '@mui/icons-material/WhatsApp';
import Facebook from '@mui/icons-material/Facebook';
import { useIntl } from 'react-intl';

import { useKioskMode } from '@/hooks/useKioskMode';

import SectionTitle from '../theme/SectionTitle';

import styles from './index.module.scss';
import PhoneSvg from '../icons/Phone';
import EmailSvg from '../icons/Email';
import SMSSvg from '../icons/SMS';
import WeChatSvg from '../icons/WeChat';
import LineSvg from '../icons/Line';
import ViberSvg from '../icons/Viber';

type ContactType =
  | 'sms'
  | 'phone'
  | 'email'
  | 'whatsapp'
  | 'facebook'
  | 'wechat'
  | 'line'
  | 'viber';

type ContactProps = {
  title?: string;
} & {
  [key in ContactType]?: {
    value: string;
    label?: string;
  };
};

export const Contact = ({
  title,
  sms,
  phone,
  email,
  whatsapp,
  facebook,
  wechat,
  line,
  viber,
}: ContactProps) => {
  const intl = useIntl();
  const { isEnabled: isKioskMode } = useKioskMode();

  const hasAnyContact =
    sms || phone || email || whatsapp || facebook || wechat || line || viber;
  if (!hasAnyContact) {
    return null;
  }

  const renderIcon = (type: string) => {
    switch (type) {
      case 'whatsapp':
        return <WhatsAppSvg />;
      case 'phone':
        return <PhoneSvg />;
      case 'email':
        return <EmailSvg />;
      case 'sms':
        return <SMSSvg />;
      case 'facebook':
        return <Facebook />;
      case 'wechat':
        return <WeChatSvg />;
      case 'line':
        return <LineSvg />;
      case 'viber':
        return <ViberSvg />;
      default:
        return <SMSSvg />;
    }
  };

  const getURL = (type: ContactType, value: string) => {
    switch (type) {
      case 'phone':
        return `tel:${value}`;
      case 'sms':
        return `sms:${value}`;
      case 'email':
        return `mailto:${value}`;
      default:
        return value;
    }
  };

  const getLabel = (type: ContactType) => {
    switch (type) {
      case 'phone':
        return intl.formatMessage({
          id: 'call',
          defaultMessage: 'Call',
        });
      case 'sms':
        return intl.formatMessage({
          id: 'sms',
          defaultMessage: 'SMS',
        });
      case 'email':
        return intl.formatMessage({
          id: 'email',
          defaultMessage: 'Email',
        });
      case 'whatsapp':
        return 'WhatsApp';
      case 'facebook':
        return 'Facebook';
      case 'wechat':
        return 'WeChat';
      case 'line':
        return 'Line';
      case 'viber':
        return 'Viber';
      default:
        return '';
    }
  };

  const renderContact = (
    type: ContactType,
    contact?: { value: string; label?: string },
  ) => {
    if (!contact?.value) {
      return null;
    }

    if (isKioskMode) {
      return (
        <div className={styles.contact}>
          {renderIcon(type)}
          <span>{contact.label || getLabel(type)}</span>
          <span style={{ overflowWrap: 'anywhere', fontSize: '0.85rem' }}>
            {contact.value}
          </span>
        </div>
      );
    }

    return (
      <a href={getURL(type, contact.value)} className={styles.contact}>
        {renderIcon(type)}
        <span>{contact.label || getLabel(type)}</span>
      </a>
    );
  };

  return (
    <div className={styles.container}>
      <SectionTitle>
        {title ||
          intl.formatMessage({
            id: 'contact_us',
            defaultMessage: 'Contact us',
          })}
      </SectionTitle>

      <div className={styles.contactGrid}>
        {renderContact('phone', phone)}
        {renderContact('sms', sms)}
        {renderContact('email', email)}
        {renderContact('whatsapp', whatsapp)}
        {renderContact('facebook', facebook)}
        {renderContact('wechat', wechat)}
        {renderContact('line', line)}
        {renderContact('viber', viber)}
      </div>
    </div>
  );
};
